/**
 * Helpers for storing/reading flight (and other travel) times as
 * timezone-agnostic *wall-clock* values.
 *
 * A flight time is local to its airport: a 21:00 departure from Italy must
 * always display as 21:00 regardless of the viewer's device timezone. Storing
 * an absolute instant (Firestore Timestamp / a UTC ISO string with a `Z`)
 * shifts the wall-clock when read in another zone, so instead we serialize the
 * local wall-clock without any UTC/offset designator (e.g.
 * "2025-08-01T21:00:00.000"). This mirrors the Flutter app, which stores these
 * times the same way.
 */

const pad = (n: number, len = 2): string => String(n).padStart(len, '0');

/** Serialize a Date's local wall-clock as an offset-less ISO string for storage. */
export function toWallClockIso(date: Date): string {
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
        `.${pad(date.getMilliseconds(), 3)}`
    );
}

/**
 * An ISO 8601 date-time whose timezone designator (`Z` or `±HH:MM`) is captured
 * separately so it can be dropped. Anchored on the full date+time shape so a
 * plain date ("2025-08-01") is never mistaken for an offset.
 */
const ZONE_DESIGNATOR = /^(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)(?:[Zz]|[+-]\d{2}:?(?:\d{2})?)$/;

/**
 * Parse a wall-clock ISO string into a Date holding that same clock reading.
 *
 * A 10:15 departure from Luxembourg is 10:15 in Luxembourg and must read 10:15
 * everywhere, so a timezone designator on the source string is **dropped, not
 * converted** — `new Date("2025-08-01T10:15:00+02:00")` would otherwise render
 * as some other hour in the viewer's timezone.
 */
export function parseWallClockIso(value: string): Date {
    const match = ZONE_DESIGNATOR.exec(value.trim());
    return new Date(match ? match[1] : value);
}

/**
 * Re-serialize a parsed time as a storable wall-clock ISO string, dropping any
 * timezone designator instead of converting it. Returns null for empty or
 * unparseable input.
 */
export function normalizeWallClockIso(value: string | Date | null | undefined): string | null {
    if (!value) return null;
    const date = value instanceof Date ? value : parseWallClockIso(value);
    return isNaN(date.getTime()) ? null : toWallClockIso(date);
}

/** Format a Date's local wall-clock as a `datetime-local` input value ("YYYY-MM-DDTHH:mm"). */
export function toDatetimeLocalValue(date: Date): string {
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
}
